import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, BadgeCheck, Flag, ShieldCheck, Image, BookOpen, TrendingUp, ScrollText, Bell } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyCity } from "@/components/CompanionCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const EMPTY = {
  name: "", slug: "", city: "johannesburg", age: 25, height: "1.70m",
  category: "Companions", race: "White",
  tagline: "", bio: "", hair: "", eyes: "", body: "", languages: "English",
  interests: "", verified: true, featured: false, hour: 2500,
  hero_image: "", gallery: "",
};

/**
 * Admin panel — Weekly Insights Digest push runner.
 * Lets admin trigger a dry-run (see who would receive it) or a real send,
 * and view recent digest run history.
 */
function DigestPanel() {
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const loadHistory = () =>
    api.get("/admin/push/weekly-digest/history")
      .then((r) => setHistory(r.data))
      .catch(() => {});

  useEffect(() => { loadHistory(); }, []);

  const run = async (dryRun) => {
    setRunning(true);
    try {
      const { data } = await api.post(`/admin/push/weekly-digest?dry_run=${dryRun ? 1 : 0}`);
      setLastResult(data);
      toast.success(
        dryRun
          ? `Dry run — would push to ${data.pushes_sent} advertisers`
          : `Sent ${data.pushes_sent} weekly digests`,
      );
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Digest failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="digest-panel">
      <div className="border border-white/10 bg-velvet-coal/40 p-8">
        <div className="flex items-start gap-4">
          <Bell size={22} className="text-velvet-gold" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-luxe text-velvet-gold">Weekly Insights digest</p>
            <h3 className="mt-2 font-heading text-3xl font-light text-white">
              Push each advertiser their week
            </h3>
            <p className="mt-3 max-w-2xl text-sm font-light text-white/60">
              Every subscribed advertiser gets a personalised push:
              &ldquo;{`{name}`} — 47 views, 3 new messages, 2 favourites this week.&rdquo;
              Auto-runs every Sunday at 09:00 SAST. Use these controls to run manually.
            </p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            onClick={() => run(true)}
            disabled={running}
            data-testid="digest-dry-run"
            className="rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-luxe text-white transition-colors hover:border-velvet-gold hover:text-velvet-gold disabled:opacity-50"
          >
            {running ? "Running…" : "Dry run"}
          </button>
          <button
            onClick={() => {
              if (!window.confirm("Send real weekly digest push to every subscribed advertiser now?")) return;
              run(false);
            }}
            disabled={running}
            data-testid="digest-real-send"
            className="rounded-full bg-velvet-gold px-6 py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-white disabled:opacity-50"
          >
            Send now
          </button>
        </div>
        {lastResult && (
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-white/10 pt-6 md:grid-cols-4">
            {[
              ["Pushed", lastResult.pushes_sent],
              ["Summarised", lastResult.advertisers_summarised],
              ["Skipped", lastResult.skipped],
              ["Ran", lastResult.ran_at ? new Date(lastResult.ran_at).toLocaleTimeString() : "—"],
            ].map(([label, val]) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-luxe text-white/40">{label}</p>
                <p className="mt-1 font-heading text-2xl font-light tabular-nums text-white">{val}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Run history</p>
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-[10px] uppercase tracking-luxe text-white/40">
              <tr>
                <th className="px-4 py-3 text-left">When</th>
                <th className="px-4 py-3 text-right">Pushed</th>
                <th className="px-4 py-3 text-right">Summarised</th>
                <th className="px-4 py-3 text-right">Skipped</th>
                <th className="px-4 py-3 text-right">Mode</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center font-light text-white/40">No runs yet.</td></tr>
              )}
              {history.map((h) => (
                <tr key={h._id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-white/80">{new Date(h.ran_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-velvet-gold">{h.pushes_sent}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/80">{h.advertisers_summarised}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-white/60">{h.skipped}</td>
                  <td className="px-4 py-3 text-right text-[10px] uppercase tracking-luxe text-white/40">
                    {h.dry_run ? "dry" : "live"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [reports, setReports] = useState([]);
  const [banners, setBanners] = useState([]);
  const [banner, setBanner] = useState({ title: "", image: "", link: "" });
  const [users, setUsers] = useState([]);
  const [adminMsgs, setAdminMsgs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [series, setSeries] = useState(null);
  const [range, setRange] = useState(30);
  const [activity, setActivity] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [vouchers, setVouchers] = useState([]);
  const [voucherForm, setVoucherForm] = useState({
    code: "", discount_type: "percent", discount_value: 20,
    expires_at: "", notes: "", active: true, package_id: "",
  });
  const [discountRequests, setDiscountRequests] = useState([]);

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) navigate("/");
  }, [user, navigate]);

  const load = useCallback(async () => {
    try {
      const [s, c, e, v, r, b, u, m, an, ts, act, vch, dr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/companions?sort=featured"),
        api.get("/admin/enquiries"),
        api.get("/admin/verifications"),
        api.get("/admin/reports"),
        api.get("/banners"),
        api.get("/admin/users"),
        api.get("/admin/messages"),
        api.get("/admin/analytics"),
        api.get(`/admin/analytics-timeseries?days=${range}`),
        api.get("/admin/activity?limit=200"),
        api.get("/admin/vouchers"),
        api.get("/admin/discount-requests"),
      ]);
      setStats(s.data);
      setCompanions(c.data);
      setEnquiries(e.data);
      setVerifications(v.data);
      setReports(r.data);
      setBanners(b.data);
      setUsers(u.data);
      setAdminMsgs(m.data);
      setAnalytics(an.data);
      setSeries(ts.data);
      setActivity(act.data);
      setVouchers(vch.data);
      setDiscountRequests(dr.data);
    } catch {}
  }, [range]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  const remove = async (slug) => {
    try {
      await api.delete(`/admin/companions/${slug}`);
      toast.success("Companion removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const verify = async (slug) => {
    await api.put(`/admin/companions/${slug}/verify`);
    toast.success("Verified");
    load();
  };

  const addBanner = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/banners", banner);
      toast.success("Banner added");
      setBanner({ title: "", image: "", link: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delBanner = async (id) => {
    await api.delete(`/admin/banners/${id}`);
    load();
  };

  const updateUser = async (id, patch) => {
    await api.put(`/admin/users/${id}`, patch);
    toast.success("Member updated");
    load();
  };

  const deleteUser = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Member deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, slug: form.slug, city: form.city, age: Number(form.age),
      height: form.height, tagline: form.tagline, bio: form.bio,
      category: form.category, race: form.race,
      attributes: { hair: form.hair, eyes: form.eyes, body: form.body },
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      interests: form.interests.split(",").map((s) => s.trim()).filter(Boolean),
      verified: form.verified, featured: form.featured,
      rates: {},
      availability: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      hero_image: form.hero_image,
      gallery: form.gallery.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      await api.post("/admin/companions", payload);
      toast.success("Companion added");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user?.role !== "admin") return null;

  const inputCls = "w-full border-b border-white/15 bg-transparent py-2.5 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none";

  return (
    <div className="pt-28 md:pt-36" data-testid="admin-page">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Admin</p>
        <h1 className="font-heading text-5xl font-light leading-none tracking-tight text-white md:text-6xl">Control room</h1>

        <div className="mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4">
          {stats && Object.entries(stats).map(([k, v]) => (
            <div key={k} className="bg-velvet-ink p-6" data-testid={`stat-${k}`}>
              <p className="font-heading text-4xl font-semibold text-white">{v}</p>
              <p className="mt-2 text-[10px] uppercase tracking-luxe text-white/40">{k}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="companions" className="mt-16">
          <TabsList className="mb-10 flex-wrap rounded-none border-b border-white/10 bg-transparent p-0">
            <TabsTrigger value="companions" data-testid="tab-companions" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Companions</TabsTrigger>
            <TabsTrigger value="verifications" data-testid="tab-verifications" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Verify ({verifications.length})</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Reports ({reports.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Enquiries</TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Banners</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Members</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-admin-messages" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Messages</TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Vouchers ({vouchers.length})</TabsTrigger>
            <TabsTrigger value="digest" data-testid="tab-digest" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Digest</TabsTrigger>
            <TabsTrigger value="discount-requests" data-testid="tab-discount-requests" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Requests ({discountRequests.filter((d) => d.status === "open").length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Analytics</TabsTrigger>
            <TabsTrigger value="guide" data-testid="tab-guide" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Guide</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Activity</TabsTrigger>
            <TabsTrigger value="add" data-testid="tab-add" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-velvet-gold text-xs uppercase tracking-luxe">Add new</TabsTrigger>
          </TabsList>

          <TabsContent value="companions">
            <div className="divide-y divide-white/10 border-y border-white/10">
              {companions.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4 py-4" data-testid={`admin-companion-${c.slug}`}>
                  <div className="flex items-center gap-4">
                    <img src={c.hero_image} alt={c.name} className="h-14 w-11 object-cover" />
                    <div>
                      <p className="text-sm text-white">{c.name} {c.featured && <span className="text-velvet-gold">★</span>}</p>
                      <p className="text-xs uppercase tracking-luxe text-white/40">{prettyCity(c.city)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!c.verified && (
                      <button onClick={() => verify(c.slug)} data-testid={`admin-verify-${c.slug}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-luxe text-white/60 hover:border-velvet-gold hover:text-velvet-gold">
                        <BadgeCheck size={12} /> Verify
                      </button>
                    )}
                    {c.verified && <BadgeCheck size={16} className="text-velvet-gold" />}
                    <button onClick={() => remove(c.slug)} data-testid={`delete-${c.slug}`} className="text-white/40 transition-colors hover:text-velvet-rose">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="verifications">
            <div className="space-y-4" data-testid="admin-verifications">
              {verifications.length === 0 && <p className="text-sm font-light text-white/40">No pending verification requests.</p>}
              {verifications.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4 border border-white/10 p-4">
                  <div className="flex items-center gap-4">
                    <img src={c.hero_image} alt={c.name} className="h-14 w-11 object-cover" />
                    <div>
                      <p className="text-sm text-white">{c.name}</p>
                      <p className="text-xs uppercase tracking-luxe text-white/40">{prettyCity(c.city)} &middot; {c.category}</p>
                    </div>
                  </div>
                  <button onClick={() => verify(c.slug)} data-testid={`approve-verify-${c.slug}`} className="flex items-center gap-2 rounded-full bg-velvet-gold px-5 py-2.5 text-[10px] uppercase tracking-luxe text-black">
                    <ShieldCheck size={13} /> Approve
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reports">
            <div className="space-y-4" data-testid="admin-reports">
              {reports.length === 0 && <p className="text-sm font-light text-white/40">No reports.</p>}
              {reports.map((r) => (
                <div key={r.id} className="border border-white/10 p-6">
                  <p className="flex items-center gap-2 text-sm text-white"><Flag size={14} className="text-velvet-rose" /> {r.companion_slug}</p>
                  <p className="mt-1 text-xs text-white/40">Reported by {r.reporter}</p>
                  <p className="mt-3 text-sm font-light text-white/60">{r.reason}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="banners">
            <form onSubmit={addBanner} className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="banner-form">
              <input required placeholder="Banner title" value={banner.title} onChange={(e) => setBanner({ ...banner, title: e.target.value })} className={inputCls} data-testid="banner-title" />
              <input placeholder="Link URL (optional)" value={banner.link} onChange={(e) => setBanner({ ...banner, link: e.target.value })} className={inputCls} />
              <input required placeholder="Image URL" value={banner.image} onChange={(e) => setBanner({ ...banner, image: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="banner-image" />
              <button type="submit" data-testid="banner-submit" className="flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-xs uppercase tracking-luxe text-black hover:bg-velvet-gold md:col-span-2"><Image size={14} /> Add banner</button>
            </form>
            <div className="space-y-3" data-testid="admin-banners">
              {banners.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-4 border border-white/10 p-3">
                  <div className="flex items-center gap-3"><img src={b.image} alt={b.title} className="h-10 w-20 object-cover" /><span className="text-sm text-white">{b.title}</span></div>
                  <button onClick={() => delBanner(b.id)} className="text-white/40 hover:text-velvet-rose"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="enquiries">
            <div className="space-y-4" data-testid="admin-enquiries">
              {enquiries.length === 0 && <p className="text-sm font-light text-white/40">No enquiries yet.</p>}
              {enquiries.map((e) => (
                <div key={e.id} className="border border-white/10 p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white">{e.name} &middot; <span className="text-white/50">{e.email}</span></p>
                    <span className="text-xs uppercase tracking-luxe text-velvet-gold">{e.companion_name}</span>
                  </div>
                  {e.phone && <p className="mt-1 text-xs text-white/40">{e.phone} {e.date && `· ${e.date}`}</p>}
                  <p className="mt-3 text-sm font-light text-white/60">{e.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="members">
            <div className="overflow-x-auto" data-testid="admin-members">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-luxe text-white/40">
                    <th className="py-3">Username</th><th>Email</th><th>Type</th><th>Membership</th><th>Role</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-white/5" data-testid={`member-${u.id}`}>
                      <td className="py-3 text-white">{u.username || "—"}</td>
                      <td className="text-white/50">{u.email}</td>
                      <td>
                        <select value={u.account_type} onChange={(e) => updateUser(u.id, { account_type: e.target.value })} className="rounded-none border border-white/15 bg-velvet-coal px-2 py-1 text-xs text-white">
                          <option value="member">member</option><option value="advertiser">advertiser</option>
                        </select>
                      </td>
                      <td>
                        <select value={u.membership} onChange={(e) => updateUser(u.id, { membership: e.target.value })} data-testid={`member-membership-${u.id}`} className="rounded-none border border-white/15 bg-velvet-coal px-2 py-1 text-xs text-white">
                          <option value="free">free</option><option value="premium">premium</option>
                        </select>
                      </td>
                      <td>
                        <select value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="rounded-none border border-white/15 bg-velvet-coal px-2 py-1 text-xs text-white">
                          <option value="user">user</option><option value="admin">admin</option>
                        </select>
                      </td>
                      <td><button onClick={() => deleteUser(u.id)} className="text-white/40 hover:text-velvet-rose"><Trash2 size={15} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="space-y-4" data-testid="admin-message-copies">
              {adminMsgs.length === 0 && <p className="text-sm font-light text-white/40">No messages yet.</p>}
              {adminMsgs.map((m) => (
                <div key={m.id} className="border border-white/10 p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white">{m.from_name} <span className="text-white/40">({m.from_email})</span></p>
                    <span className="text-xs uppercase tracking-luxe text-velvet-gold">
                      {m.kind === "conv_reply" ? "reply in" : "to"} {m.companion_name || m.listing_name || "conversation"}
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-light text-white/60">{m.body}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-luxe text-white/30">{new Date(m.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="vouchers">
            <div className="space-y-8" data-testid="admin-vouchers-section">
              {/* Create form */}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await api.post("/admin/vouchers", {
                      ...voucherForm,
                      discount_value: Number(voucherForm.discount_value),
                    });
                    toast.success("Voucher created");
                    setVoucherForm({
                      code: "", discount_type: "percent", discount_value: 20,
                      expires_at: "", notes: "", active: true, package_id: "",
                    });
                    load();
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create voucher");
                  }
                }}
                className="border border-white/10 bg-velvet-coal/40 p-6"
                data-testid="voucher-create-form"
              >
                <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">Issue new voucher</p>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">Code (blank = auto)</label>
                    <input
                      value={voucherForm.code}
                      onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })}
                      placeholder="VIPINVITE"
                      data-testid="voucher-form-code"
                      className="w-full border-b border-white/15 bg-transparent py-2 text-sm uppercase tracking-luxe text-white focus:border-velvet-gold focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">Discount type</label>
                    <select
                      value={voucherForm.discount_type}
                      onChange={(e) => setVoucherForm({ ...voucherForm, discount_type: e.target.value })}
                      data-testid="voucher-form-type"
                      className="w-full border-b border-white/15 bg-velvet-coal py-2 text-sm text-white focus:border-velvet-gold focus:outline-none"
                    >
                      <option value="percent">Percent %</option>
                      <option value="fixed">Fixed ZAR</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">
                      Value {voucherForm.discount_type === "percent" ? "(%)" : "(ZAR)"}
                    </label>
                    <input
                      type="number"
                      min={1}
                      step="0.01"
                      value={voucherForm.discount_value}
                      onChange={(e) => setVoucherForm({ ...voucherForm, discount_value: e.target.value })}
                      data-testid="voucher-form-value"
                      className="w-full border-b border-white/15 bg-transparent py-2 text-sm text-white focus:border-velvet-gold focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">Expiry (optional)</label>
                    <input
                      type="date"
                      value={voucherForm.expires_at}
                      onChange={(e) => setVoucherForm({ ...voucherForm, expires_at: e.target.value })}
                      data-testid="voucher-form-expires"
                      className="w-full border-b border-white/15 bg-velvet-coal py-2 text-sm text-white focus:border-velvet-gold focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">Limit to package (optional)</label>
                    <select
                      value={voucherForm.package_id}
                      onChange={(e) => setVoucherForm({ ...voucherForm, package_id: e.target.value })}
                      data-testid="voucher-form-package"
                      className="w-full border-b border-white/15 bg-velvet-coal py-2 text-sm text-white focus:border-velvet-gold focus:outline-none"
                    >
                      <option value="">Any package</option>
                      <option value="premium">Premium Membership</option>
                      <option value="boost_week">Featured — 1 Week</option>
                      <option value="boost_month">Featured — 1 Month</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-luxe text-white/40">Notes / Invited by</label>
                    <input
                      value={voucherForm.notes}
                      onChange={(e) => setVoucherForm({ ...voucherForm, notes: e.target.value })}
                      placeholder="VIP launch invite"
                      data-testid="voucher-form-notes"
                      className="w-full border-b border-white/15 bg-transparent py-2 text-sm text-white focus:border-velvet-gold focus:outline-none"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="voucher-create-submit"
                  className="mt-6 rounded-full bg-velvet-gold px-8 py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-white"
                >
                  Create voucher
                </button>
              </form>

              {/* List */}
              <div className="border border-white/10">
                <table className="w-full text-sm">
                  <thead className="border-b border-white/10 text-[10px] uppercase tracking-luxe text-white/40">
                    <tr>
                      <th className="px-4 py-3 text-left">Code</th>
                      <th className="px-4 py-3 text-left">Discount</th>
                      <th className="px-4 py-3 text-left">Package</th>
                      <th className="px-4 py-3 text-left">Expires</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Notes</th>
                      <th className="px-4 py-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center font-light text-white/40">
                          No vouchers yet.
                        </td>
                      </tr>
                    )}
                    {vouchers.map((v) => (
                      <tr key={v.id} className="border-b border-white/5" data-testid={`voucher-row-${v.code}`}>
                        <td className="px-4 py-3 font-mono text-velvet-gold">{v.code}</td>
                        <td className="px-4 py-3 text-white">
                          {v.discount_type === "percent" ? `${v.discount_value}%` : `R${v.discount_value}`}
                        </td>
                        <td className="px-4 py-3 text-white/60">{v.package_id || "Any"}</td>
                        <td className="px-4 py-3 text-white/60">{v.expires_at || "—"}</td>
                        <td className="px-4 py-3">
                          {v.used ? (
                            <span className="text-velvet-rose">Redeemed</span>
                          ) : v.active ? (
                            <span className="text-[#25D366]">Active</span>
                          ) : (
                            <span className="text-white/40">Paused</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white/50">{v.notes}</td>
                        <td className="px-4 py-3 text-right">
                          {!v.used && (
                            <button
                              onClick={async () => {
                                try {
                                  await api.put(`/admin/vouchers/${v.id}/toggle-active`);
                                  load();
                                } catch {}
                              }}
                              data-testid={`voucher-toggle-${v.code}`}
                              className="mr-3 text-[10px] uppercase tracking-luxe text-white/60 hover:text-velvet-gold"
                            >
                              {v.active ? "Pause" : "Resume"}
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Delete voucher ${v.code}?`)) return;
                              try {
                                await api.delete(`/admin/vouchers/${v.id}`);
                                toast.success("Deleted");
                                load();
                              } catch {}
                            }}
                            data-testid={`voucher-delete-${v.code}`}
                            className="text-[10px] uppercase tracking-luxe text-velvet-rose hover:text-white"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="discount-requests">
            <div className="space-y-4" data-testid="admin-discount-requests">
              {discountRequests.length === 0 && (
                <p className="py-10 text-center text-sm font-light text-white/40">No discount requests yet.</p>
              )}
              {discountRequests.map((r) => (
                <div key={r.id} className="border border-white/10 p-6" data-testid={`discount-request-${r.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-white">{r.name} <span className="text-white/40">({r.email})</span></p>
                      {r.package_id && (
                        <p className="mt-1 text-[10px] uppercase tracking-luxe text-velvet-gold">
                          For package: {r.package_id}
                        </p>
                      )}
                    </div>
                    <span
                      className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-luxe ${
                        r.status === "open"
                          ? "border-velvet-gold text-velvet-gold"
                          : r.status === "fulfilled"
                          ? "border-[#25D366] text-[#25D366]"
                          : "border-white/20 text-white/40"
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                  <p className="mt-3 text-sm font-light text-white/70">{r.reason}</p>
                  <p className="mt-2 text-[10px] uppercase tracking-luxe text-white/30">
                    {new Date(r.created_at).toLocaleString()}
                  </p>

                  {r.status === "open" && (
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <input
                        placeholder="Voucher code to send"
                        id={`send-code-${r.id}`}
                        data-testid={`discount-request-code-${r.id}`}
                        className="min-w-[200px] flex-1 border-b border-white/15 bg-transparent py-2 text-sm uppercase tracking-luxe text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
                      />
                      <button
                        onClick={async () => {
                          const el = document.getElementById(`send-code-${r.id}`);
                          const code = (el?.value || "").trim().toUpperCase();
                          try {
                            await api.put(`/admin/discount-requests/${r.id}/fulfill`, {
                              voucher_code: code, message: "",
                            });
                            toast.success(code ? "Voucher sent" : "Request declined");
                            load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not fulfill");
                          }
                        }}
                        data-testid={`discount-request-send-${r.id}`}
                        className="rounded-full bg-velvet-gold px-5 py-2 text-[10px] uppercase tracking-luxe text-black transition-colors hover:bg-white"
                      >
                        Send code
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await api.put(`/admin/discount-requests/${r.id}/fulfill`, { voucher_code: "", message: "" });
                            toast.success("Declined");
                            load();
                          } catch {}
                        }}
                        data-testid={`discount-request-decline-${r.id}`}
                        className="rounded-full border border-white/15 px-5 py-2 text-[10px] uppercase tracking-luxe text-white/60 hover:border-velvet-rose hover:text-velvet-rose"
                      >
                        Decline
                      </button>
                    </div>
                  )}
                  {r.status === "fulfilled" && r.voucher_code && (
                    <p className="mt-3 text-[10px] uppercase tracking-luxe text-velvet-gold">
                      Sent voucher: <span className="font-mono">{r.voucher_code}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="digest">
            <DigestPanel />
          </TabsContent>


          <TabsContent value="analytics">
            {analytics && series && (
              <div className="space-y-12" data-testid="admin-analytics">
                {/* KPI cards */}
                <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-6">
                  {[
                    ["Profiles", analytics.total_profiles],
                    ["Views", analytics.total_views],
                    ["Favourites", analytics.total_favourites],
                    ["Messages", analytics.total_messages],
                    ["Premium", analytics.premium_members],
                    ["Advertisers", analytics.advertisers],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-velvet-ink p-5">
                      <p className="font-heading text-3xl font-semibold text-white">{v}</p>
                      <p className="mt-1 text-[10px] uppercase tracking-luxe text-white/40">{k}</p>
                    </div>
                  ))}
                </div>

                {/* Range picker + revenue */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Total revenue ({series.range_days}d)</p>
                    <p className="mt-1 font-heading text-4xl font-semibold text-white">R {series.total_revenue.toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1 rounded-none border border-white/10 p-1" data-testid="analytics-range">
                    {[7, 30, 90].map((d) => (
                      <button key={d} onClick={() => setRange(d)}
                        data-testid={`range-${d}`}
                        className={`px-4 py-2 text-[10px] uppercase tracking-luxe transition-colors ${
                          range === d ? "bg-velvet-gold text-black" : "text-white/60 hover:text-white"
                        }`}>{d}d</button>
                    ))}
                  </div>
                </div>

                {/* Signups + Listings area chart */}
                <div className="border border-white/10 p-6" data-testid="chart-signups">
                  <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Signups &amp; Listings over time</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={series.series}>
                      <defs>
                        <linearGradient id="gSignups" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#C15F76" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#C15F76" stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id="gListings" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#D2A857" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#D2A857" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#ffffff10" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#ffffff60", fontSize: 10 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                      <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em", color: "#ffffff80" }} />
                      <Area type="monotone" dataKey="signups" stroke="#C15F76" strokeWidth={2} fill="url(#gSignups)" />
                      <Area type="monotone" dataKey="listings" stroke="#D2A857" strokeWidth={2} fill="url(#gListings)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Revenue + payments line chart */}
                <div className="border border-white/10 p-6" data-testid="chart-revenue">
                  <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Revenue (ZAR) &amp; payments</p>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={series.series}>
                      <CartesianGrid stroke="#ffffff10" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis yAxisId="left" tick={{ fill: "#D2A857", fontSize: 10 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: "#C15F76", fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                      <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em", color: "#ffffff80" }} />
                      <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#D2A857" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="payments" stroke="#C15F76" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Split pies + messages bar */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="border border-white/10 p-6" data-testid="chart-members-split">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Members vs Advertisers</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={[
                          { name: "Members", value: series.members },
                          { name: "Advertisers", value: series.advertisers },
                        ]} innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                          <Cell fill="#C15F76" />
                          <Cell fill="#D2A857" />
                        </Pie>
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em", color: "#ffffff80" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="border border-white/10 p-6" data-testid="chart-membership-split">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Free vs Premium</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={[
                          { name: "Free", value: series.free },
                          { name: "Premium", value: series.premium },
                        ]} innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                          <Cell fill="#ffffff30" />
                          <Cell fill="#D2A857" />
                        </Pie>
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em", color: "#ffffff80" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="border border-white/10 p-6" data-testid="chart-messages">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Messages / day</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={series.series}>
                        <CartesianGrid stroke="#ffffff10" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: "#ffffff60", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} />
                        <YAxis tick={{ fill: "#ffffff60", fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Bar dataKey="messages" fill="#C15F76" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Distribution bars */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="border border-white/10 p-6" data-testid="chart-cities">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">Profiles by city</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={analytics.by_city.slice(0, 10)} layout="vertical">
                        <XAxis type="number" tick={{ fill: "#ffffff60", fontSize: 10 }} allowDecimals={false} />
                        <YAxis dataKey="name" type="category" tick={{ fill: "#ffffff80", fontSize: 10 }} width={110} />
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Bar dataKey="count" fill="#D2A857" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="border border-white/10 p-6" data-testid="chart-categories">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">By category</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={analytics.by_category}>
                        <CartesianGrid stroke="#ffffff10" vertical={false} />
                        <XAxis dataKey="name" tick={{ fill: "#ffffff60", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#ffffff60", fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Bar dataKey="count" fill="#C15F76" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="border border-white/10 p-6" data-testid="chart-genders">
                    <p className="mb-4 text-xs uppercase tracking-luxe text-velvet-gold">By gender</p>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={analytics.by_gender || []} outerRadius={90} dataKey="count" nameKey="name" label={{ fill: "#fff", fontSize: 10 }}>
                          {(analytics.by_gender || []).map((_, i) => (
                            <Cell key={i} fill={["#D2A857", "#C15F76", "#8B5CF6", "#22D3EE", "#F97316", "#94A3B8"][i % 6]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#1A0810", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }} />
                        <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.15em", color: "#ffffff80" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Top-viewed leaderboard */}
                <div>
                  <p className="mb-5 text-xs uppercase tracking-luxe text-velvet-gold">Most viewed profiles</p>
                  <div className="space-y-3">
                    {analytics.top_viewed.map((c, i) => (
                      <div key={c.slug} className="flex items-center justify-between border-b border-white/5 pb-2">
                        <span className="flex items-center gap-3 text-sm text-white">
                          <span className="text-white/30">{i + 1}</span>
                          <img src={c.hero_image} alt="" className="h-8 w-6 object-cover" /> {c.name}
                        </span>
                        <span className="text-sm text-velvet-gold">{c.views} views</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="guide">
            <div className="max-w-4xl space-y-8" data-testid="admin-guide">
              <div>
                <p className="text-xs uppercase tracking-luxe text-velvet-gold">Admin manual</p>
                <h2 className="mt-3 font-heading text-4xl font-light text-white md:text-5xl">
                  How to run <span className="italic text-velvet-rose">Vixera</span>
                </h2>
                <p className="mt-4 text-sm font-light text-white/60">
                  Every workflow you need — companions, users, verifications, payments and content — in one place.
                </p>
              </div>

              {[
                {
                  title: "Approving verifications",
                  steps: [
                    "Open the 'Verify' tab — it shows every pending selfie-code verification.",
                    "Click a profile to review the selfie image and confirm the code matches.",
                    "Hit 'Verify' to approve (adds the gold badge) or 'Reject' to send them back to redo.",
                    "Verified profiles rank higher in the directory and are trusted by members.",
                  ],
                },
                {
                  title: "Managing members and advertisers",
                  steps: [
                    "Go to 'Members' — every registered user is listed.",
                    "Change role (user ↔ admin), membership (free ↔ premium), or account_type (member ↔ advertiser) with the inline dropdowns.",
                    "Use the trash icon to permanently delete a user (cascade deletes their listings and messages).",
                    "Search by email/username with the top filter.",
                  ],
                },
                {
                  title: "Reading Analytics (graphs)",
                  steps: [
                    "'Analytics' tab. Choose a range (7 / 30 / 90 days) at the top-right.",
                    "Area chart = signups & listings over time.",
                    "Line chart = revenue and payments; use two Y axes.",
                    "Pie charts = Members vs Advertisers and Free vs Premium splits.",
                    "Bar charts = cities, categories, gender distribution.",
                    "Numbers refresh on every page load.",
                  ],
                },
                {
                  title: "Handling reports (moderation)",
                  steps: [
                    "'Reports' tab shows open flags from members.",
                    "Click through to the profile in question. Suspend by editing the listing, or delete under Companions tab.",
                    "Once resolved, delete the report row to keep the queue clean.",
                  ],
                },
                {
                  title: "Adding a blog post",
                  steps: [
                    "POST /api/admin/blog with {title, excerpt, body, image}. UI editor is on the Journal roadmap.",
                    "Titles auto-slugify. Existing slugs are overwritten (upsert).",
                    "Posts appear on /journal instantly.",
                  ],
                },
                {
                  title: "Publishing homepage banners",
                  steps: [
                    "'Banners' tab. Provide title + image URL + optional link URL.",
                    "Toggle active/inactive by deleting or recreating.",
                    "Banners are shown on the marketing surfaces (GET /api/banners).",
                  ],
                },
                {
                  title: "Payments & refunds",
                  steps: [
                    "All PayFast transactions are in payment_transactions collection.",
                    "For refunds, use the PayFast Dashboard and reference the m_payment_id (visible in Analytics revenue drill-down).",
                    "Vixera does not auto-refund — this is intentional so the admin has final say.",
                  ],
                },
                {
                  title: "Membership tiers",
                  steps: [
                    "Free (R0): can browse and message BUT search-by-name only in Directory.",
                    "Premium (R350/mo): full filter access — city, gender, race, category, vibes, verified, available now.",
                    "Advertiser: automatically has premium-level search + can place ads.",
                    "Admins have unrestricted access everywhere.",
                  ],
                },
                {
                  title: "Deleting your own admin",
                  steps: [
                    "You CANNOT self-delete an admin via /api/auth/me — it returns 400. This is a safety guard.",
                    "To reassign admin, use the Members tab: change another user's role to 'admin' first, then log in as them to demote the current admin.",
                  ],
                },
              ].map((section) => (
                <div key={section.title} className="border border-white/10 p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <BookOpen size={16} className="text-velvet-gold" />
                    <h3 className="font-heading text-xl font-light text-white">{section.title}</h3>
                  </div>
                  <ol className="ml-5 space-y-2 text-sm font-light text-white/70">
                    {section.steps.map((s, i) => (
                      <li key={i} className="list-decimal">{s}</li>
                    ))}
                  </ol>
                </div>
              ))}

              <div className="border border-velvet-gold/40 bg-velvet-gold/5 p-6">
                <p className="text-xs uppercase tracking-luxe text-velvet-gold">Reference doc</p>
                <p className="mt-2 text-sm font-light text-white/70">
                  A copy of this guide is also stored at <code className="text-velvet-gold">/app/memory/ADMIN_GUIDE.md</code> for offline reference.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="space-y-6" data-testid="admin-activity">
              <div>
                <p className="text-xs uppercase tracking-luxe text-velvet-gold">Moderator audit trail</p>
                <h3 className="mt-3 font-heading text-3xl font-light text-white md:text-4xl">
                  Every <span className="italic text-velvet-rose">moderation action</span>
                </h3>
                <p className="mt-3 max-w-xl text-sm font-light text-white/50">
                  Every verify, reject, delete, and role-change is stamped with who, what, when — for accountability and compliance.
                </p>
              </div>

              {activity.length === 0 ? (
                <p className="border border-white/10 p-12 text-center text-sm font-light text-white/40">
                  No moderation actions logged yet.
                </p>
              ) : (
                <div className="overflow-x-auto border border-white/10">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-white/10 bg-white/[0.03]">
                      <tr className="text-[10px] uppercase tracking-luxe text-white/50">
                        <th className="px-4 py-3">When</th>
                        <th className="px-4 py-3">Actor</th>
                        <th className="px-4 py-3">Action</th>
                        <th className="px-4 py-3">Target</th>
                        <th className="px-4 py-3">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((a) => {
                        const color = {
                          verify: "text-velvet-gold",
                          reject_verification: "text-velvet-rose",
                          delete_companion: "text-velvet-rose",
                          delete_user: "text-velvet-rose",
                          delete_banner: "text-velvet-rose",
                          update_user: "text-white",
                        }[a.action] || "text-white/60";
                        const icon = {
                          verify: <ShieldCheck size={12} />,
                          reject_verification: <Flag size={12} />,
                          delete_companion: <Trash2 size={12} />,
                          delete_user: <Trash2 size={12} />,
                          delete_banner: <Trash2 size={12} />,
                          update_user: <ScrollText size={12} />,
                        }[a.action] || <ScrollText size={12} />;
                        return (
                          <tr key={a.id} data-testid={`activity-row-${a.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                            <td className="whitespace-nowrap px-4 py-3 text-xs text-white/50">
                              {new Date(a.created_at).toLocaleString("en-ZA", { dateStyle: "short", timeStyle: "short" })}
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <p className="text-white">{a.actor_username || a.actor_email}</p>
                              <p className="text-[10px] uppercase tracking-luxe text-white/40">{a.actor_role}</p>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <span className={`inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 uppercase tracking-luxe ${color}`}>
                                {icon} {a.action.replace(/_/g, " ")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-white/70">
                              <span className="text-[10px] uppercase tracking-luxe text-white/40">{a.target_type}</span>
                              <br />
                              <span className="font-mono text-[11px] text-white/80">{a.target_id}</span>
                            </td>
                            <td className="px-4 py-3 text-xs font-light text-white/50">
                              {a.meta && Object.keys(a.meta).length > 0
                                ? Object.entries(a.meta).map(([k, v]) => (
                                    <div key={k}><b className="text-white/70">{k}</b>: {String(v)}</div>
                                  ))
                                : <span className="text-white/30">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] uppercase tracking-luxe text-white/30">
                Showing the last {activity.length} actions. Older entries remain in the database.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="add">
            <form onSubmit={create} className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="add-companion-form">
              <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="add-name" />
              <input required placeholder="Slug (e.g. jane-jhb)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputCls} data-testid="add-slug" />
              <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="add-city">
                <option className="bg-velvet-coal" value="johannesburg">Johannesburg</option>
                <option className="bg-velvet-coal" value="cape-town">Cape Town</option>
                <option className="bg-velvet-coal" value="durban">Durban</option>
                <option className="bg-velvet-coal" value="pretoria">Pretoria</option>
              </select>
              <input type="number" placeholder="Age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={inputCls} data-testid="add-age" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="add-category">
                {["Companions", "Models", "Masseuses", "Dancers", "Duos", "Personals"].map((c) => <option key={c} value={c} className="bg-velvet-coal">{c}</option>)}
              </select>
              <select value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })} className={inputCls} data-testid="add-race">
                {["African", "White", "Coloured", "Indian", "Asian", "Latina", "Mixed", "Other"].map((r) => <option key={r} value={r} className="bg-velvet-coal">{r}</option>)}
              </select>
              <input placeholder="Tagline" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="add-tagline" />
              <textarea placeholder="Bio" rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="add-bio" />
              <input placeholder="Hair" value={form.hair} onChange={(e) => setForm({ ...form, hair: e.target.value })} className={inputCls} />
              <input placeholder="Eyes" value={form.eyes} onChange={(e) => setForm({ ...form, eyes: e.target.value })} className={inputCls} />
              <input placeholder="Figure" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} />
              <input placeholder="Languages (comma separated)" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} className={inputCls} />
              <input placeholder="Interests (comma separated)" value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} className={inputCls} />
              <input required placeholder="Hero image URL" value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className={`${inputCls} md:col-span-2`} data-testid="add-hero" />
              <input placeholder="Gallery URLs (comma separated)" value={form.gallery} onChange={(e) => setForm({ ...form, gallery: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <div className="flex items-center gap-8 md:col-span-2">
                <label className="flex items-center gap-2 text-xs uppercase tracking-luxe text-white/60">
                  <input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} /> Verified
                </label>
                <label className="flex items-center gap-2 text-xs uppercase tracking-luxe text-white/60">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
                </label>
              </div>
              <button type="submit" data-testid="add-submit" className="flex w-fit items-center gap-2 rounded-full bg-white px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold hover:text-white md:col-span-2">
                <Plus size={15} /> Add companion
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
